import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import OntologyBrowser, {
    BrowserSource, CodeText, OntologyColumn, OntologyFilter, ScopeLevelChip, SCOPE_LEVEL_FILTER,
} from './OntologyBrowser';
import { sourceDocumentsApi, normalizeDocumentList } from '../../api/ontologyApi';
import { DOC_KIND, docClassLabel, refTypeLabel } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * The Documents tab: the registry a citation points at.
 *
 * This is the FOURTH entry point into the library, and the one that was missing. An
 * analyst could already arrive term-first, policy-first or threshold-first; nobody could
 * arrive document-first — "we are revising this policy, what does it touch?" — because a
 * citation was a display string with no reverse index.
 *
 * Read-only by design: documents are registered upstream, so there is no create, approve
 * or supersede here and no Status filter, because everything in the registry is approved.
 */

const columns: OntologyColumn[] = [
    {
        key: 'title',
        label: 'Document',
        width: '34%',
        render: (doc) => (
            <Box>
                <Typography component="div" sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                    {doc.title || doc.source_doc_code}
                    {doc.version_label && (
                        <Typography component="span" sx={{ ml: 0.75, fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                            {doc.version_label}
                        </Typography>
                    )}
                </Typography>
                {/* The reference printed ON the document, which is what someone reads off
                    a PDF — not the registry code, which only this system uses. */}
                <CodeText>{doc.document_identifier || doc.source_doc_code}</CodeText>
            </Box>
        ),
    },
    {
        key: 'summary',
        label: 'What it covers',
        width: '26%',
        render: (doc) => (
            <Typography
                component="div"
                sx={{
                    fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.55,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {doc.summary || '—'}
            </Typography>
        ),
    },
    {
        key: 'issuer',
        label: 'Issued by',
        render: (doc) => (
            <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                {doc.issuer || '—'}
                {doc.jurisdiction && (
                    <Typography component="span" sx={{ ml: 0.75, fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                        {doc.jurisdiction}
                    </Typography>
                )}
            </Typography>
        ),
    },
    {
        key: 'ref_type',
        label: 'Type',
        render: (doc) => (
            <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                {refTypeLabel(doc.ref_type) || '—'}
                {doc.doc_class && (
                    <Typography component="span" sx={{ ml: 0.75, fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                        {docClassLabel(doc.doc_class)}
                    </Typography>
                )}
            </Typography>
        ),
    },
    {
        key: 'governs_concepts',
        label: 'Names',
        /**
         * The document's own forward declaration.
         *
         * Shown on the LIST because it is the honest preview of the blast radius. An
         * institution's own policy is typically cited by nothing — it is what the library
         * was derived from — so a "cited by" count would read zero for exactly the
         * documents somebody is most likely to be revising.
         */
        render: (doc) => {
            const count = Array.isArray(doc.governs_concepts) ? doc.governs_concepts.length : 0;
            return (
                <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                    {count === 0 ? '—' : `${count} term${count === 1 ? '' : 's'}`}
                </Typography>
            );
        },
    },
    {
        key: 'scope_level',
        label: 'Scope',
        render: (doc) => <ScopeLevelChip scopeLevel={doc.scope_level} />,
    },
];

const REF_TYPE_FILTER: OntologyFilter = {
    key: 'ref_type',
    label: 'Type',
    options: [
        { value: '', label: 'All types' },
        { value: 'regulatory_guidance', label: 'Guidance' },
        { value: 'statute', label: 'Statute' },
        { value: 'regulation', label: 'Regulation' },
        { value: 'standard', label: 'Standard' },
        { value: 'internal_policy', label: 'Internal policy' },
        { value: 'procedure', label: 'Procedure' },
    ],
};

const DOC_CLASS_FILTER: OntologyFilter = {
    key: 'doc_class',
    label: 'Origin',
    options: [
        { value: '', label: 'Published and internal' },
        { value: 'external_publication', label: 'Published' },
        { value: 'internal_document', label: 'Our own' },
    ],
};

interface DocumentsBrowserProps {
    filters: Record<string, string>;
    onRowSelect?: (doc: Record<string, any>) => void;
    selectedCode?: string | null;
}

const DocumentsBrowser: React.FC<DocumentsBrowserProps> = ({
    filters, onRowSelect, selectedCode,
}) => {
    const source: BrowserSource = useMemo(() => ({
        list: (params, options) => sourceDocumentsApi.list(params, options),
        normalize: normalizeDocumentList,
        codeOf: (doc) => doc.source_doc_code,
        detailBase: `/ontology/${DOC_KIND}`,
        label: 'source documents',
    }), []);

    return (
        <OntologyBrowser
            // No `kind`: source documents are deliberately not an OntologyKind, so
            // apiForKind, codeOf and normalizeList do not know them. `source` supplies
            // all four in their place.
            source={source}
            // The registry has no binding to an activity template, so the agent filter
            // would send a parameter /source-documents does not implement.
            showActivityFilter={false}
            filters={filters}
            columns={columns}
            filterControls={[REF_TYPE_FILTER, DOC_CLASS_FILTER, SCOPE_LEVEL_FILTER]}
            searchPlaceholder="Search title, identifier or issuer…"
            emptyMessage="No documents match these filters."
            onRowSelect={onRowSelect}
            selectedCode={selectedCode}
        />
    );
};

export default DocumentsBrowser;
